import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Address shown on the legal pages for privacy questions and deletion requests.
const CONTACT_EMAIL = 'contact@youshop.pro';

// Renders a legal page (Privacy Policy, Data Deletion) from the content stored
// under `legal.<page>` in the translations.
export default function LegalPage({ page }) {
  const { t, isRTL } = useLanguage();
  const content = t(`legal.${page}`);

  // Router navigation keeps the previous scroll position, so start at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const arrowIcon = (
    <svg
      className={`h-4 w-4 ${isRTL ? '' : 'rotate-180'}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="section-padding relative overflow-hidden bg-navy-900 pt-32">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand-500/10 blur-3xl" />

        <div className="relative mx-auto max-w-3xl text-center">
          <div className="mb-8">
            <Link
              to="/home"
              className="inline-flex items-center gap-2 text-sm font-medium text-navy-200 transition-colors hover:text-white"
            >
              <svg
                className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {t('legal.back')}
            </Link>
          </div>

          <span className="inline-block rounded-full border border-brand-500/40 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold tracking-wider text-brand-400">
            {t('legal.badge')}
          </span>
          <h1 className="mt-6 text-4xl font-bold text-white sm:text-5xl">{content.title}</h1>
          <p className="mt-4 text-sm text-navy-300">{t('legal.lastUpdated')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 py-16 sm:px-8 lg:py-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-lg leading-relaxed text-navy-600">{content.intro}</p>

          {content.sections.map((section, i) => {
            const List = section.ordered ? 'ol' : 'ul';
            return (
              <div key={i} className="mt-12">
                <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-800">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-sm font-bold text-brand-500">
                    {i + 1}
                  </span>
                  {section.title}
                </h2>

                {section.body?.map((paragraph, j) => (
                  <p key={j} className="mt-4 leading-relaxed text-navy-500">{paragraph}</p>
                ))}

                {section.items && (
                  <List
                    className={`mt-4 space-y-3 ps-5 leading-relaxed text-navy-500 marker:text-brand-500 ${
                      section.ordered ? 'list-decimal marker:font-semibold' : 'list-disc'
                    }`}
                  >
                    {section.items.map((item, j) => (
                      <li key={j} className="ps-1">
                        {typeof item === 'string' ? item : (
                          <>
                            <strong className="font-semibold text-navy-700">{item.label}</strong> {item.text}
                          </>
                        )}
                      </li>
                    ))}
                  </List>
                )}

                {section.note && (
                  <p className="mt-4 leading-relaxed text-navy-500">{section.note}</p>
                )}

                {section.emailSubject && (
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(section.emailSubject)}`}
                    className="btn-primary mt-6 inline-flex items-center gap-2 !px-6 !py-3 text-sm"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span dir="ltr">{CONTACT_EMAIL}</span>
                  </a>
                )}

                {section.link && (
                  <div className="mt-4">
                    <Link
                      to={section.link.to}
                      className="inline-flex items-center gap-2 font-semibold text-brand-500 transition-colors hover:text-brand-600"
                    >
                      {section.link.label}
                      {arrowIcon}
                    </Link>
                  </div>
                )}
              </div>
            );
          })}

          {/* Contact */}
          <div className="mt-16 rounded-2xl border border-navy-100 bg-navy-50 p-8">
            <h2 className="text-xl font-bold text-navy-800">{t('legal.contactTitle')}</h2>
            <p className="mt-2 leading-relaxed text-navy-500">{t('legal.contactDesc')}</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              dir="ltr"
              className="mt-4 inline-block text-lg font-semibold text-brand-500 transition-colors hover:text-brand-600"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
